import * as THREE from 'three';
import { mergeGeometries, mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';

// Grayscale vertex factors preserve the player's palette while separating hems,
// seams and recessed areas without a new material or draw call per detail.
export function shade(geo, factor = 1) {
  const p = geo.attributes.position, values = [];
  for (let i = 0; i < p.count; i++) {
    const f = typeof factor === 'function' ? factor(p.getX(i), p.getY(i), p.getZ(i)) : factor;
    values.push(f, f, f);
  }
  geo.setAttribute('color', new THREE.Float32BufferAttribute(values, 3));
  return geo;
}

export function combine(geos) {
  const prepared = geos.map(g => {
    g.deleteAttribute('uv');
    if (!g.attributes.color) shade(g);
    return g.index ? g : mergeVertices(g);
  });
  const result = mergeGeometries(prepared);
  new Set([...geos, ...prepared]).forEach(g => g.dispose());
  return result;
}

export function patch(points, indices) {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(points.flat(), 3));
  geo.setIndex(indices); geo.computeVertexNormals();
  return geo;
}

// Closed ribbon with a small real thickness. Width is measured perpendicular to
// the path in XZ; suitable for shoe straps and seams following curved surfaces.
export function ribbon(points, width, thickness = .012) {
  const positions = [], indices = [];
  const v = points.map(p => new THREE.Vector3(...p));
  for (let i = 0; i < v.length; i++) {
    const tangent = v[Math.min(i + 1, v.length - 1)].clone().sub(v[Math.max(i - 1, 0)]);
    const side = new THREE.Vector3(-tangent.z, 0, tangent.x).normalize().multiplyScalar(width / 2);
    for (const offset of [-thickness / 2, thickness / 2]) for (const sign of [-1, 1]) {
      positions.push(v[i].x + sign * side.x, v[i].y + offset, v[i].z + sign * side.z);
    }
    if (i < v.length - 1) {
      const k = i * 4;
      indices.push(k, k + 4, k + 1, k + 1, k + 4, k + 5,
        k + 2, k + 3, k + 6, k + 3, k + 7, k + 6,
        k, k + 2, k + 4, k + 2, k + 6, k + 4,
        k + 1, k + 5, k + 3, k + 3, k + 5, k + 7);
    }
  }
  const k = (v.length - 1) * 4;
  indices.push(0, 1, 2, 1, 3, 2, k, k + 2, k + 1, k + 1, k + 2, k + 3);
  return patch(Array.from({ length: positions.length / 3 }, (_, i) => positions.slice(i * 3, i * 3 + 3)), indices);
}
